import React, { ChangeEvent, FunctionComponent, MouseEventHandler } from 'react'
import { CalendarToday, Clear } from '@mui/icons-material'
import useControlled from '../../lib/hooks/useControlled'
import { formatWithSeparators, getRegexpIndices } from '../../lib/utils'
import IconButton from '../IconButton'
import Input from '../Input'
import { CommonProps } from '../helperTypes'

type Props = {
  /** disabled state */
  disabled?: boolean
  /** optional react ref to pass to native input */
  inputRef?: React.RefObject<HTMLInputElement>
  /** calendar icon button click handler */
  onCalendarClick?: MouseEventHandler<HTMLButtonElement>
  /** input change handler */
  onChange?: (date: string) => void
  /** optional react ref to pass to native open button */
  openBtnRef?: React.RefObject<HTMLButtonElement>
  /**
   * Custom date pattern
   * @default [0-9]{2}/[0-9]{2}/[0-9]{4}
   */
  pattern?: string
  /**
   * pattern & placeholder separator
   * set this if you use a custom pattern with another separator
   * @default "/"
   */
  separator?: string
  /** show the calendar icon button */
  showCalendarButton?: boolean
  /** show the clear icon button */
  showClearButton?: boolean
  /** text value */
  value?: string
}

type DateInputProps = Props & CommonProps

const DateInput: FunctionComponent<DateInputProps> = (props) => {
  const {
    defaultValue,
    disabled = undefined,
    inputRef,
    onCalendarClick,
    onChange,
    onClick,
    openBtnRef,
    pattern = '[0-9]{2}/[0-9]{2}/[0-9]{4}',
    placeholder = 'dd/mm/yyyy',
    separator = '/',
    showCalendarButton,
    showClearButton,
    value: valueProp,
    ...rest
  } = props
  const [value, setValue] = useControlled({
    value: valueProp,
    defaultValue: defaultValue,
  })

  // keep saved input cursor position on rerender
  const [cursor, setCursor] = React.useState<number>(-1)
  React.useEffect(() => {
    if (cursor < 0) return
    if (inputRef && inputRef.current) {
      inputRef.current.selectionStart = inputRef.current.selectionEnd = cursor
    }
  }, [cursor, inputRef, value])

  // compute separator indices in placeholder
  const separatorRegexp = React.useMemo(() => new RegExp(`\\${separator}`, 'g'), [separator])
  const separatorIndices: number[] = React.useMemo(() => {
    return getRegexpIndices(placeholder, separatorRegexp)
  }, [placeholder, separatorRegexp])

  // limit typing to numbers, separator and special keys
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.ctrlKey || event.metaKey) return
    // don't limit if pattern includes alphanumeric
    const patternHasAlphaChars = pattern.match(/[a-zA-Z]/)
    if (patternHasAlphaChars) return

    const { key } = event
    const allowedSpecialKeys = [separator, 'Backspace', 'Meta', 'ArrowDown', 'ArrowUp', 'Tab']
    if (allowedSpecialKeys.indexOf(key) < 0 && !key.match(/[0-9]/)) event.preventDefault()
  }
  const handleChange = ({ nativeEvent, target }: ChangeEvent<HTMLInputElement>) => {
    let { value: newValue, selectionStart } = target
    const { inputType } = nativeEvent as InputEvent

    // update separators automatically by following placeholder structure
    if (inputType && inputType.indexOf('inser') >= 0) {
      // cursor moved inside the date string > restart from here
      if (selectionStart && value.length > selectionStart) {
        newValue = newValue.slice(0, selectionStart)
        selectionStart++
      }
      newValue = formatWithSeparators(newValue, placeholder, separator, separatorRegexp, separatorIndices)
    }
    if (selectionStart !== null && separatorIndices.indexOf(selectionStart) >= 0 && inputType.indexOf('delete') < 0) {
      // jump cursor over separators
      setCursor(selectionStart + 1)
    } else {
      // save cursor position
      setCursor(selectionStart !== null ? selectionStart : -1)
    }
    setValue(newValue)
    onChange && onChange(newValue)
  }
  const clear = () => {
    setValue('')
    onChange && onChange('')
  }
  return (
    <Input
      {...rest}
      disabled={disabled}
      endAdornment={
        <>
          {showClearButton && value !== '' && (
            <IconButton variant='ghost' size='xsmall' aria-label='Clear date' onClick={() => clear()}>
              <Clear />
            </IconButton>
          )}
          {showCalendarButton && (
            <IconButton
              disabled={disabled}
              variant='ghost'
              size='small'
              aria-label='Open calendar'
              onClick={onCalendarClick}
              tabIndex={-1}
              ref={openBtnRef}
            >
              <CalendarToday style={{ pointerEvents: 'none' }} />
            </IconButton>
          )}
        </>
      }
      onKeyDownCapture={handleKeyDown}
      onChange={handleChange}
      onClick={onClick}
      pattern={pattern}
      placeholder={placeholder}
      inputRef={inputRef}
      value={value || ''}
    />
  )
}

export default DateInput
